import { translate } from "game/translate";
import './styles.css';
import { DifficultyLevelType } from "constants/difficulty";
import { Difficulty } from "./Difficulty";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setCurrentDifficulty } from "ui/store/actions/gameUIActions";
import { useDispatch, useSelector } from "react-redux";
import { UIRootState } from "ui/store/reducers";
import { Store } from "game/store/gameStore";

interface Props {
    difficulties: {type: DifficultyLevelType, key: string}[];
    dataDraw?: string;
    dataSuit?: string;
    modalRef: React.RefObject<HTMLDivElement>;
    gameUiRef?: React.RefObject<HTMLDivElement>;
    activeTabKey?: string;
}

export const DifficultiesList = ({ difficulties, dataDraw, dataSuit, modalRef, gameUiRef, activeTabKey }: Props) => {
    const dispatch = useDispatch();
    const selectedDifficulty = useSelector((state: UIRootState) => state.gameUI.modals.newGameModal?.currentDifficulty);
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = useRef<number | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [isTooltipSuppressed, setIsTooltipSuppressed] = useState(false);
    const didInitialScrollRef = useRef(false);

    const scrollToSelected = useCallback(() => {
        const container = contentRef.current;
        if (!container) return;

        const targetEl = container.querySelector('.newgame-difficulty-container--selected') as HTMLElement | null;
        if (targetEl) {
            const containerRect = container.getBoundingClientRect();
            const targetRect = targetEl.getBoundingClientRect();
            const desiredTop = targetRect.top - containerRect.top + container.scrollTop;
            const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
            container.scrollTop = Math.min(Math.max(0, desiredTop), maxScrollTop);
        }
    }, []);
    const balancedDifficulty = useMemo(() => (!Store.isPyramid ? { key: 'balanced', type: "_BALANCED" } : undefined), [Store.isPyramid]);
    
    const initialDifficulty = useMemo(() => {
        if (!difficulties.find(x => x.type === Store.difficulty)) {
            const storeName = Store.getStoreNameByType(Store.name, parseInt(dataDraw || '0'), parseInt(dataSuit || '0'));
            const saved = localStorage.getItem('difficulty_' + storeName);

            const splitedValue = saved && saved.split('_').length >= 1 ? saved.split('_')[1] : undefined;
            const savedItem = difficulties.find(x => splitedValue && x.type.toLowerCase().includes(splitedValue.toLocaleLowerCase()))
            
            return savedItem?.type || balancedDifficulty?.type || difficulties[0]?.type || undefined;
        }

        return Store.difficulty;
    }, [difficulties]);

    useEffect(() => {
        if (Store.suits === (parseInt(dataSuit || '1')) && Store.dealCount === (parseInt(dataDraw || '1')) && Store.difficultyBalanced && !Store.isPyramid) {
            dispatch(setCurrentDifficulty({ type: "_BALANCED" as DifficultyLevelType, dataDraw, dataSuit }));
            return;
        }
        dispatch(setCurrentDifficulty({ type: initialDifficulty as DifficultyLevelType, dataDraw, dataSuit }));
    }, [initialDifficulty]);

    // Initial scroll on mount only
    useEffect(() => {
        if (didInitialScrollRef.current) return;
        const timer = setTimeout(() => {
            scrollToSelected();
            didInitialScrollRef.current = true;
        }, 0);
        return () => clearTimeout(timer);
    }, []);

    // Scroll when tab changes
    useEffect(() => {
        const timer = setTimeout(() => {
            scrollToSelected();
        }, 0);
        return () => clearTimeout(timer);
    }, [activeTabKey, scrollToSelected]);


    const handleSelectDifficulty = (difficulty: DifficultyLevelType) => {
        dispatch(setCurrentDifficulty({ type: difficulty, dataDraw, dataSuit }));
    };

    const handleScroll = () => {
        if (!isScrolling) {
            setIsScrolling(true);
        }
        if (scrollTimeoutRef.current) {
            window.clearTimeout(scrollTimeoutRef.current);
        }
        if (!isTooltipSuppressed) {
            setIsTooltipSuppressed(true);
        }
        scrollTimeoutRef.current = window.setTimeout(() => {
            setIsScrolling(false);
        }, 200);
    };

    const handleIconPointerLeave = () => {
        if (isTooltipSuppressed) {
            setIsTooltipSuppressed(false);
        }
    };



    return (
        <div className="newgame-difficulties-list-container">
            <span className="newgame-difficulties-list-title">{translate('select_difficulty')}</span>
            <div ref={contentRef} onScroll={handleScroll} className={`newgame-difficulties-list-content${isScrolling ? ' is-scrolling' : ''}${isTooltipSuppressed ? ' suppress-tooltips' : ''}`}>
                {([balancedDifficulty, ...difficulties].filter(Boolean) as { key: string, type: DifficultyLevelType }[]).map((difficulty) => (
                    <Difficulty 
                        key={difficulty.key} 
                        difficultyKey={difficulty.key} 
                        type={difficulty.type} 
                        title={translate(difficulty.key)} 
                        isSelected={difficulty.type === selectedDifficulty?.type} 
                        onSelectDifficulty={handleSelectDifficulty} 
                        onIconPointerLeave={handleIconPointerLeave} 
                        modalRef={modalRef} 
                        gameUiRef={gameUiRef} 
                    />
                ))}
            </div>
        </div>
    );
};